import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { Loader2, Tv, WifiOff } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const TV_TOKEN_KEY = 'elevated_tv_token';

const Brand = ({ className = '' }) => (
  <div className={`flex items-center gap-3 ${className}`}>
    <img
      src="https://customer-assets.emergentagent.com/job_arena-coach-1/artifacts/zh1vb354_Elevated%20Logo.png"
      alt="Elevated Roping Media"
      className="h-12 md:h-16 w-auto drop-shadow-[0_0_18px_rgba(217,119,74,0.35)]"
    />
    <div className="text-left leading-none">
      <div className="font-black text-white text-xl md:text-2xl tracking-tight">ELEVATED</div>
      <div className="font-bold text-white/60 text-xs md:text-sm tracking-[0.25em] mt-1">
        ROPING MEDIA
      </div>
    </div>
  </div>
);

/**
 * TVPage — meant to be opened in the smart TV's built-in browser.
 * Flow:
 *   1) On mount, check localStorage for an existing tv_token
 *   2) If token exists → try to play the live stream
 *   3) If no token OR token invalid → request a new code from backend, show
 *      the code big + instruction ("Go to elevatedarena.com/tv/pair on your
 *      phone and enter this code"), then poll status every 2s
 *   4) Once claimed → save tv_token, load live stream
 *
 * No login form on TV. No typing on the remote. Ever.
 */
const TVPage = () => {
  const [phase, setPhase] = useState('boot'); // boot | pairing | live | offline | error
  const [pairCode, setPairCode] = useState('');
  const [playerUrl, setPlayerUrl] = useState('');
  const [streamTitle, setStreamTitle] = useState('');
  const [userEmail, setUserEmail] = useState('');
  const [errorMsg, setErrorMsg] = useState('');
  const pollRef = useRef(null);
  const liveRef = useRef(null);

  // ---------- Bootstrap: check for free-for-all → existing token → pairing ----------
  useEffect(() => {
    let cancelled = false;
    const boot = async () => {
      // 1) If this stream is organizer-sponsored (free_for_all), skip pairing
      //    entirely — TV shows the stream immediately, no code required.
      //    Even if no embed URL is pasted yet, we still bypass login and show
      //    a "starting soon" placeholder until the admin sets one.
      try {
        const pub = await axios.get(`${API_URL}/api/live-stream`, { params: { tv: 1 }, timeout: 10000 });
        if (cancelled) return;
        if (pub.data?.free_for_all) {
          setStreamTitle(pub.data.title || 'Live Event');
          setPlayerUrl(pub.data.player_url || '');
          setUserEmail('Free access — sponsored event');
          setPhase(pub.data.player_url ? 'live' : 'offline');
          startFreeLivePolling();
          return;
        }
      } catch (_) {
        // fall through to normal pairing
      }

      const existing = localStorage.getItem(TV_TOKEN_KEY);
      if (existing) {
        try {
          const r = await axios.get(`${API_URL}/api/tv/live-stream`, { params: { tv_token: existing } });
          if (cancelled) return;
          setUserEmail(r.data.user_email || '');
          setStreamTitle(r.data.title || 'Live Event');
          setPlayerUrl(r.data.player_url || '');
          setPhase(r.data.is_live ? 'live' : 'offline');
          startLivePolling(existing);
          return;
        } catch (err) {
          // Only nuke the saved token on 401 (paired user gone / expired).
          // Transient 5xx must not un-pair — fall through to fresh pairing
          // this session but keep the token so the next reload has a chance.
          if (err.response?.status === 401) {
            localStorage.removeItem(TV_TOKEN_KEY);
          }
        }
      }
      requestNewCode();
    };
    boot();
    return () => {
      cancelled = true;
      if (pollRef.current) clearInterval(pollRef.current);
      if (liveRef.current) clearInterval(liveRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const requestNewCode = async (attempt = 0) => {
    setPhase('pairing');
    setPlayerUrl('');
    if (pollRef.current) clearInterval(pollRef.current);
    try {
      const r = await axios.post(`${API_URL}/api/tv/pair/start`, null, { timeout: 15000 });
      setPairCode(r.data.code);
      pollRef.current = setInterval(() => pollStatus(r.data.code), 2000);
    } catch (err) {
      // Transient network / Cloudflare 5xx / cold start — silently retry
      // with exponential backoff. Only surface a "retry" screen after ~1 min
      // of continuous failure, so a normal blip never scares the user.
      const status = err.response?.status;
      const isTransient = !status || status >= 500 || status === 408 || status === 429;
      if (isTransient && attempt < 6) {
        const delay = Math.min(2000 * Math.pow(1.6, attempt), 20000);
        setPairCode('');
        setTimeout(() => requestNewCode(attempt + 1), delay);
        return;
      }
      setErrorMsg(err.response?.data?.detail || err.message || 'Could not get a pair code');
      setPhase('error');
    }
  };

  const pollStatus = async (code) => {
    try {
      const r = await axios.get(`${API_URL}/api/tv/pair/status`, { params: { code } });
      if (r.data.expired) {
        clearInterval(pollRef.current);
        requestNewCode();
        return;
      }
      if (r.data.claimed && r.data.tv_token) {
        clearInterval(pollRef.current);
        localStorage.setItem(TV_TOKEN_KEY, r.data.tv_token);
        // Now fetch live stream (retry on transient 5xx before showing error)
        const loadStream = async (tv_token, attempt = 0) => {
          try {
            const lr = await axios.get(`${API_URL}/api/tv/live-stream`, { params: { tv_token }, timeout: 15000 });
            setUserEmail(lr.data.user_email || '');
            setStreamTitle(lr.data.title || 'Live Event');
            setPlayerUrl(lr.data.player_url || '');
            setPhase(lr.data.is_live ? 'live' : 'offline');
            startLivePolling(tv_token);
          } catch (err) {
            const status = err.response?.status;
            const isTransient = !status || status >= 500 || status === 408 || status === 429;
            if (isTransient && attempt < 5) {
              const delay = Math.min(2000 * Math.pow(1.6, attempt), 15000);
              setTimeout(() => loadStream(tv_token, attempt + 1), delay);
              return;
            }
            setErrorMsg(err.response?.data?.detail || 'Failed to load stream');
            setPhase('error');
          }
        };
        loadStream(r.data.tv_token);
      }
    } catch (_) { /* keep polling */ }
  };

  const startLivePolling = (tv_token) => {
    if (liveRef.current) clearInterval(liveRef.current);
    liveRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/tv/live-stream`, { params: { tv_token } });
        setStreamTitle(r.data.title || 'Live Event');
        if (r.data.player_url !== playerUrl) setPlayerUrl(r.data.player_url || '');
        setPhase(r.data.is_live ? 'live' : 'offline');
      } catch (err) {
        if (err.response?.status === 401) {
          clearInterval(liveRef.current);
          localStorage.removeItem(TV_TOKEN_KEY);
          requestNewCode();
        }
      }
    }, 30000);
  };

  // Poll the public endpoint for free-for-all streams (no token needed).
  const startFreeLivePolling = () => {
    if (liveRef.current) clearInterval(liveRef.current);
    liveRef.current = setInterval(async () => {
      try {
        const r = await axios.get(`${API_URL}/api/live-stream`, { params: { tv: 1 } });
        // If organizer turned off free_for_all mid-event, drop back to pairing
        if (!r.data?.free_for_all) {
          clearInterval(liveRef.current);
          requestNewCode();
          return;
        }
        setStreamTitle(r.data.title || 'Live Event');
        if (r.data.player_url !== playerUrl) setPlayerUrl(r.data.player_url || '');
        // If a URL wasn't set at boot, flip to 'live' the moment one appears
        setPhase(r.data.player_url ? 'live' : 'offline');
      } catch (_) { /* keep polling */ }
    }, 15000);
  };

  const pairHostname = typeof window !== 'undefined' ? window.location.host : 'elevatedarena.com';
  const pairOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://elevatedarena.com';
  const pairUrl = pairCode ? `${pairOrigin}/tv/pair?code=${pairCode}` : `${pairOrigin}/tv/pair`;

  return (
    <div className="fixed inset-0 bg-black text-white overflow-hidden">
      {phase === 'boot' && (
        <div className="flex items-center justify-center h-full">
          <Loader2 className="w-16 h-16 animate-spin" />
        </div>
      )}

      {phase === 'pairing' && (
        <div className="flex flex-col items-center justify-center h-full px-8 text-center gap-8">
          <Brand />
          <div className="flex items-center gap-4 -mt-2">
            <Tv className="w-12 h-12 md:w-14 md:h-14 text-primary" />
            <div className="text-left">
              <div className="text-3xl md:text-5xl font-black tracking-tight uppercase">Watch on TV</div>
              <div className="text-white/70 text-base md:text-xl">Live Events — Anywhere</div>
            </div>
          </div>

          <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-12">
            {/* QR code — the fast path */}
            <div className="bg-white rounded-3xl p-6 md:p-8 shadow-2xl" data-testid="tv-pair-qr">
              {pairCode ? (
                <QRCodeSVG
                  value={pairUrl}
                  size={260}
                  level="M"
                  bgColor="#ffffff"
                  fgColor="#000000"
                  includeMargin={false}
                />
              ) : (
                <div className="w-[260px] h-[260px] flex items-center justify-center text-black/40">
                  <Loader2 className="w-10 h-10 animate-spin" />
                </div>
              )}
              <div className="text-black text-center text-sm font-bold mt-3 tracking-wide">
                SCAN WITH PHONE
              </div>
            </div>

            {/* Manual code — the fallback */}
            <div className="flex flex-col items-center gap-4">
              <div className="text-white/50 uppercase tracking-widest text-xs">or type it in</div>
              <div className="bg-white/5 border-4 border-primary rounded-3xl p-6 md:p-8 min-w-[280px]">
                <div className="text-white/60 uppercase tracking-widest text-xs md:text-sm mb-3">Enter this code</div>
                <div
                  className="font-mono font-black text-5xl md:text-7xl tracking-widest text-primary select-all"
                  data-testid="tv-pair-code"
                >
                  {pairCode || '------'}
                </div>
              </div>
              <div className="text-white/60 text-sm md:text-base font-mono break-all max-w-[280px]">
                {pairHostname}/tv/pair
              </div>
            </div>
          </div>

          <div className="text-white/70 text-base md:text-lg max-w-2xl">
            Point your phone camera at the QR code, or go to{' '}
            <span className="font-mono text-primary">{pairHostname}/tv/pair</span> and enter the code.
            Sign in as an active subscriber to finish.
          </div>

          <div className="text-white/40 text-sm">Code expires in 15 minutes. Waiting for pairing…</div>
        </div>
      )}

      {phase === 'live' && playerUrl && (
        <div className="w-full h-full">
          <iframe
            src={playerUrl}
            title="Elevated Live Stream"
            className="w-full h-full border-0"
            allow="autoplay; fullscreen; picture-in-picture; encrypted-media"
            allowFullScreen
            data-testid="tv-live-player"
          />
          <div className="absolute top-3 right-3 bg-black/60 backdrop-blur px-3 py-1.5 rounded-full text-xs">
            <span className="text-red-500 font-bold mr-2">● LIVE</span>
            <span className="text-white/70">{userEmail}</span>
          </div>
        </div>
      )}

      {phase === 'offline' && (
        <div className="flex flex-col items-center justify-center h-full px-8 text-center gap-8">
          <Brand />
          <div className="w-20 h-20 rounded-full border-2 border-white/15 flex items-center justify-center">
            <WifiOff className="w-10 h-10 text-white/40" />
          </div>
          <div>
            <div className="text-4xl md:text-5xl font-black uppercase tracking-tight mb-3">No Live Stream Right Now</div>
            <div className="text-white/60 text-lg max-w-xl mx-auto">
              This TV is paired to <span className="font-mono text-white/80">{userEmail}</span>. It&apos;ll start playing
              automatically when the next live event begins.
            </div>
          </div>
          <div className="text-white/40 text-sm">Checking every 30 seconds…</div>
        </div>
      )}

      {phase === 'error' && (
        <div className="flex flex-col items-center justify-center h-full px-8 text-center gap-6">
          <Brand />
          <div className="text-3xl font-bold text-red-400">Something went wrong</div>
          <div className="text-white/70 max-w-lg">{errorMsg}</div>
          <button
            onClick={requestNewCode}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-full font-bold"
            data-testid="tv-retry-btn"
          >
            Try Again
          </button>
        </div>
      )}
    </div>
  );
};

export default TVPage;
